import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import Badge from "@/ui/atoms/Badge";
import Typography from "@/ui/atoms/Typography";
import { Icon } from "@proda-ai/ui-kit";
import { IconBox } from "@proda-ai/ui-kit";

export type AccountWithTypeProps = Omit<BoxProps, "children"> & {
    type: "income" | "expense" | "non-operating" | null;
    name: string;
};

export const testIds = {
    main: "AccountWithType",
    badge: "AccountWithType-badge",
    badgeWrapper: "AccountWithType-badgeWrapper",
    badgeText: "AccountWithType-badgeText",
    text: "AccountWithType-text",
    textWrapper: "AccountWithType-textWrapper",
};
const AccountWithType = (props: AccountWithTypeProps) => {
    const { sx, type, name, className, ...otherProps } = props;

    const severity =
        type === "income"
            ? "success"
            : type === "expense"
              ? "error"
              : type === "non-operating"
                ? "inactive"
                : null;

    return (
        <Box
            data-testid={testIds.main}
            className={clsx([testIds.main, className])}
            sx={{
                position: "relative",
                width: "100%",
                height: "46px",
                gap: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingRight: "16px",
                paddingLeft: "16px",
                ...sx,
            }}
            {...otherProps}
        >
            {severity ? (
                <Box
                    data-testid={testIds.badgeWrapper}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexGrow: 0,
                        marginRight: "8px",
                    }}
                >
                    <Badge severity={severity} data-testid={testIds.badge}>
                        <Box
                            component={"span"}
                            data-testid={testIds.badgeText}
                            sx={{
                                textTransform: "capitalize",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {type === "non-operating" ? "Below the line" : type}
                        </Box>
                    </Badge>
                </Box>
            ) : (
                <Box
                    sx={{
                        flexBasis: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexGrow: 0,
                        fontSize: "12px",
                    }}
                >
                    <IconBox>
                        <Icon icon="ban" color="var(--proda-navy-blue)" />
                    </IconBox>
                </Box>
            )}
            <Box
                data-testid={testIds.textWrapper}
                sx={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    verticalAlign: "middle",
                    textAlign: "left",
                    lineHeight: "24px",
                    paddingRight: "8px",
                    flexGrow: 1,
                }}
            >
                <Typography
                    data-testid={testIds.text}
                    variant="body2"
                    sx={{
                        color:
                            type === "non-operating"
                                ? "var(--text-color-disabled)"
                                : "var(--proda-black)",
                    }}
                >
                    {name}
                </Typography>
            </Box>
        </Box>
    );
};

export default AccountWithType;
